import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, CheckCircle2, Shield, Target, Users, Database, FileText, AlertTriangle, TrendingUp, Lock, ExternalLink } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';

const ICADetail = () => {
  return (
    <div style={{ background: 'var(--bg-primary)', minHeight: '100vh' }}>
      <Header />
      <main style={{ paddingTop: '180px' }}>
        {/* Hero Section */}
        <section style={{
          background: 'var(--bg-primary)',
          padding: '80px 7.6923%',
          position: 'relative'
        }}>
          <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
            <Link to="/" style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px',
              color: 'var(--brand-primary)',
              textDecoration: 'none',
              fontSize: '16px',
              marginBottom: '30px',
              transition: 'all 0.3s'
            }}
            onMouseEnter={(e) => e.currentTarget.style.transform = 'translateX(-5px)'}
            onMouseLeave={(e) => e.currentTarget.style.transform = 'translateX(0)'}
            >
              <ArrowLeft size={20} />
              Back to Solutions
            </Link>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '60px', alignItems: 'center' }} className="detail-grid">
              <div>
                <div style={{
                  marginBottom: '30px'
                }}>
                  <h2 style={{
                    fontSize: '48px',
                    fontWeight: '700',
                    color: 'var(--brand-primary)',
                    marginBottom: '10px',
                    letterSpacing: '2px'
                  }}>ICA</h2>
                  <p style={{
                    fontSize: '18px',
                    color: 'var(--text-primary)',
                    fontWeight: '500'
                  }}>Compliance for the Digital Age</p>
                </div>
                <h1 className="display-large" style={{ marginBottom: '20px' }}>
                  Solving Technology & <span style={{ color: 'var(--brand-primary)' }}>InfoSec Compliance</span>
                </h1>
                <p className="body-large" style={{ marginBottom: '30px' }}>
                  Powered by CXp - Your Intelligent Compliance Navigator
                </p>
              </div>
              <div>
                <img 
                  src="https://images.unsplash.com/photo-1573164713988-8665fc963095" 
                  alt="Compliance Management" 
                  style={{ width: '100%', height: '400px', objectFit: 'cover' }}
                />
              </div>
            </div>
          </div>
        </section>

        {/* The Compliance Challenge */}
        <section style={{ background: 'var(--bg-primary)', padding: '80px 7.6923%' }}>
          <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
            <h2 className="display-medium" style={{ marginBottom: '30px', textAlign: 'center' }}>
              The <span style={{ color: 'var(--brand-primary)' }}>Compliance Challenge</span>
            </h2>
            <p className="body-large" style={{ textAlign: 'center', maxWidth: '900px', margin: '0 auto 60px' }}>
              Many Ways Compliance Can Become a High Risk Affair!
            </p>

            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
              gap: '40px'
            }}>
              <div style={{
                padding: '50px 40px',
                background: 'rgba(255, 255, 255, 0.03)',
                border: '1px solid var(--border-subtle)',
                transition: 'all 0.4s ease-in-out'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = 'var(--brand-primary)';
                e.currentTarget.style.transform = 'translateY(-10px)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = 'var(--border-subtle)';
                e.currentTarget.style.transform = 'translateY(0)';
              }}
              >
                <AlertTriangle size={40} color="var(--brand-primary)" style={{ marginBottom: '20px' }} />
                <h3 className="heading-2" style={{ marginBottom: '16px' }}>Operational Inefficiency</h3>
                <ul style={{ listStyle: 'none', padding: 0 }}>
                  <li style={{ marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <div style={{ width: '6px', height: '6px', background: 'var(--brand-primary)', borderRadius: '50%' }} />
                    <span className="body-small">Complex processes</span>
                  </li>
                  <li style={{ marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <div style={{ width: '6px', height: '6px', background: 'var(--brand-primary)', borderRadius: '50%' }} />
                    <span className="body-small">Lack of visibility</span>
                  </li>
                  <li style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <div style={{ width: '6px', height: '6px', background: 'var(--brand-primary)', borderRadius: '50%' }} />
                    <span className="body-small">Poor collaboration</span>
                  </li>
                </ul>
              </div>

              <div style={{
                padding: '50px 40px',
                background: 'rgba(255, 255, 255, 0.03)',
                border: '1px solid var(--border-subtle)',
                transition: 'all 0.4s ease-in-out'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = 'var(--brand-primary)';
                e.currentTarget.style.transform = 'translateY(-10px)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = 'var(--border-subtle)';
                e.currentTarget.style.transform = 'translateY(0)';
              }}
              >
                <TrendingUp size={40} color="var(--brand-primary)" style={{ marginBottom: '20px' }} />
                <h3 className="heading-2" style={{ marginBottom: '16px' }}>Evolving Landscape & Complexity</h3>
                <ul style={{ listStyle: 'none', padding: 0 }}>
                  <li style={{ marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <div style={{ width: '6px', height: '6px', background: 'var(--brand-primary)', borderRadius: '50%' }} />
                    <span className="body-small">Inconsistent approach</span>
                  </li>
                  <li style={{ marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <div style={{ width: '6px', height: '6px', background: 'var(--brand-primary)', borderRadius: '50%' }} />
                    <span className="body-small">Difficult management</span>
                  </li>
                  <li style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <div style={{ width: '6px', height: '6px', background: 'var(--brand-primary)', borderRadius: '50%' }} />
                    <span className="body-small">Hard to demonstrate</span>
                  </li>
                </ul>
              </div>

              <div style={{
                padding: '50px 40px',
                background: 'rgba(255, 255, 255, 0.03)',
                border: '1px solid var(--border-subtle)',
                transition: 'all 0.4s ease-in-out'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = 'var(--brand-primary)';
                e.currentTarget.style.transform = 'translateY(-10px)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = 'var(--border-subtle)';
                e.currentTarget.style.transform = 'translateY(0)';
              }}
              >
                <Target size={40} color="var(--brand-primary)" style={{ marginBottom: '20px' }} />
                <h3 className="heading-2" style={{ marginBottom: '16px' }}>Resource & Cost Constraints</h3>
                <ul style={{ listStyle: 'none', padding: 0 }}>
                  <li style={{ marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <div style={{ width: '6px', height: '6px', background: 'var(--brand-primary)', borderRadius: '50%' }} />
                    <span className="body-small">High costs</span>
                  </li>
                  <li style={{ marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <div style={{ width: '6px', height: '6px', background: 'var(--brand-primary)', borderRadius: '50%' }} />
                    <span className="body-small">Limited scalability</span>
                  </li>
                  <li style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <div style={{ width: '6px', height: '6px', background: 'var(--brand-primary)', borderRadius: '50%' }} />
                    <span className="body-small">Misalignment</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* The Solution */}
        <section style={{ background: 'var(--bg-primary)', padding: '80px 7.6923%' }}>
          <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
            <h2 className="display-medium" style={{ marginBottom: '30px', textAlign: 'center' }}>
              Charting a Course to <span style={{ color: 'var(--brand-primary)' }}>Compliance with ICA</span>
            </h2>
            <p className="body-large" style={{ textAlign: 'center', maxWidth: '900px', margin: '0 auto 60px' }}>
              We resolve your Technology & InfoSec compliance scenario with a unique combination of our own Technology Platform, served with Managed Services!
            </p>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '40px' }} className="solution-grid">
              <div style={{
                padding: '60px',
                background: 'rgba(0, 255, 209, 0.05)',
                border: '2px solid var(--brand-primary)'
              }}>
                <Shield size={60} color="var(--brand-primary)" style={{ marginBottom: '24px' }} />
                <h3 className="heading-1" style={{ marginBottom: '16px' }}>CXp</h3>
                <p className="body-small" style={{ color: 'var(--brand-primary)', marginBottom: '12px', fontWeight: '600' }}>
                  The Technology Enabler
                </p>
                <p className="body-medium">
                  Our innovative, scalable platform automates compliance processes, enabling seamless collaboration and elevating overall operational efficiency.
                </p>
              </div>

              <div style={{
                padding: '60px',
                background: 'rgba(255, 255, 255, 0.03)',
                border: '1px solid var(--border-subtle)'
              }}>
                <Users size={60} color="var(--brand-primary)" style={{ marginBottom: '24px' }} />
                <h3 className="heading-1" style={{ marginBottom: '16px' }}>CMaaS</h3>
                <p className="body-small" style={{ color: 'var(--brand-primary)', marginBottom: '12px', fontWeight: '600' }}>
                  The Human Expertise Enabler
                </p>
                <p className="body-medium">
                  Leverage our expertise to proactively manage the complexities of staying compliant.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Know CXp */}
        <section style={{ background: 'var(--bg-primary)', padding: '80px 7.6923%' }}>
          <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
            <h2 className="display-medium" style={{ marginBottom: '60px', textAlign: 'center' }}>
              Know <span style={{ color: 'var(--brand-primary)' }}>CXp</span> - Your Intelligent Compliance Navigator
            </h2>

            <div style={{ display: 'grid', gap: '30px' }}>
              {[
                { icon: <Target size={32} />, title: 'Process', desc: 'Conquer compliance challenges with automated workflows.' },
                { icon: <Database size={32} />, title: 'Visibility', desc: 'Gain unparalleled control over your compliance data in a single, secure command center.' },
                { icon: <Lock size={32} />, title: 'Approach', desc: 'Outsmart emerging risks with real-time insights and proactive alerts.' },
                { icon: <TrendingUp size={32} />, title: 'Management', desc: 'Unlock data-driven decisions with powerful reporting and analytics.' },
                { icon: <Users size={32} />, title: 'Collaboration', desc: 'Ignite collaboration and break down communication silos across your organization.' },
                { icon: <Shield size={32} />, title: 'Dashboards', desc: 'Dynamic dashboards with 40+ widgets for customization.' }
              ].map((item, index) => (
                <div key={index} style={{
                  padding: '30px',
                  background: 'rgba(255, 255, 255, 0.03)',
                  border: '1px solid var(--border-subtle)',
                  display: 'flex',
                  gap: '24px',
                  alignItems: 'flex-start',
                  transition: 'all 0.3s'
                }}
                onMouseEnter={(e) => e.currentTarget.style.borderColor = 'var(--brand-primary)'}
                onMouseLeave={(e) => e.currentTarget.style.borderColor = 'var(--border-subtle)'}
                >
                  <div style={{ color: 'var(--brand-primary)', flexShrink: 0 }}>
                    {item.icon}
                  </div>
                  <div>
                    <h3 className="heading-3" style={{ marginBottom: '8px' }}>{item.title}</h3>
                    <p className="body-small">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Key Features */}
        <section style={{ background: 'var(--bg-primary)', padding: '80px 7.6923%' }}>
          <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
            <h2 className="display-medium" style={{ marginBottom: '60px', textAlign: 'center' }}>
              Key <span style={{ color: 'var(--brand-primary)' }}>Features</span> - Equipped to Sustain Compliance
            </h2>

            <div style={{ display: 'grid', gap: '40px' }}>
              {/* Feature 1 */}
              <div style={{
                padding: '40px',
                background: 'rgba(255, 255, 255, 0.03)',
                border: '1px solid var(--border-subtle)'
              }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '20px', marginBottom: '20px' }}>
                  <div style={{
                    width: '48px',
                    height: '48px',
                    background: 'rgba(0, 255, 209, 0.1)',
                    border: '1px solid var(--brand-primary)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                    fontSize: '24px',
                    fontWeight: '600',
                    color: 'var(--brand-primary)'
                  }}>1</div>
                  <div>
                    <h3 className="heading-2" style={{ marginBottom: '12px' }}>Control Base & Framework Module</h3>
                    <p className="body-medium" style={{ marginBottom: '20px' }}>
                      GRC foundation, supporting all standards and regulations.
                    </p>
                  </div>
                </div>
                
                <div style={{ marginBottom: '20px' }}>
                  <p className="body-small" style={{ color: 'var(--brand-primary)', fontWeight: '600', marginBottom: '8px' }}>
                    Key Differentiators:
                  </p>
                  <ul style={{ listStyle: 'none', padding: 0 }}>
                    <li style={{ marginBottom: '8px', paddingLeft: '20px', position: 'relative' }}>
                      <CheckCircle2 size={16} color="var(--brand-primary)" style={{ position: 'absolute', left: 0, top: '4px' }} />
                      <span className="body-small">UCF support: Simplified multi-framework compliance</span>
                    </li>
                    <li style={{ paddingLeft: '20px', position: 'relative' }}>
                      <CheckCircle2 size={16} color="var(--brand-primary)" style={{ position: 'absolute', left: 0, top: '4px' }} />
                      <span className="body-small">Custom bases: Tailored to your specific needs</span>
                    </li>
                  </ul>
                </div>

                <div style={{ marginBottom: '20px' }}>
                  <p className="body-small" style={{ color: 'var(--brand-primary)', fontWeight: '600', marginBottom: '8px' }}>
                    Pain Points Addressed:
                  </p>
                  <ul style={{ listStyle: 'none', padding: 0 }}>
                    <li style={{ marginBottom: '8px', paddingLeft: '20px', position: 'relative' }}>
                      <div style={{ width: '6px', height: '6px', background: 'var(--brand-primary)', borderRadius: '50%', position: 'absolute', left: '5px', top: '8px' }} />
                      <span className="body-small">Complex multi-framework management</span>
                    </li>
                    <li style={{ paddingLeft: '20px', position: 'relative' }}>
                      <div style={{ width: '6px', height: '6px', background: 'var(--brand-primary)', borderRadius: '50%', position: 'absolute', left: '5px', top: '8px' }} />
                      <span className="body-small">Inflexible control base adaptation</span>
                    </li>
                  </ul>
                </div>

                <div>
                  <p className="body-small" style={{ color: 'var(--text-muted)', marginBottom: '12px', fontWeight: '600' }}>
                    Supported Frameworks:
                  </p>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
                    {['UCF', 'ISO 27001', 'RBI Circulars', 'GDPR', 'PCI DSS', 'SOX', 'AICPA SOC 404', 'CIS Controls', 'NIST', 'COBIT', 'ISO 27701'].map((framework, idx) => (
                      <span key={idx} style={{
                        padding: '6px 12px',
                        background: 'rgba(0, 255, 209, 0.1)',
                        border: '1px solid var(--brand-primary)',
                        fontSize: '12px',
                        color: 'var(--brand-primary)'
                      }}>
                        {framework}
                      </span>
                    ))}
                  </div>
                </div>
              </div>

              {/* Feature 2 */}
              <div style={{
                padding: '40px',
                background: 'rgba(255, 255, 255, 0.03)',
                border: '1px solid var(--border-subtle)'
              }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '20px', marginBottom: '20px' }}>
                  <div style={{
                    width: '48px',
                    height: '48px',
                    background: 'rgba(0, 255, 209, 0.1)',
                    border: '1px solid var(--brand-primary)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                    fontSize: '24px',
                    fontWeight: '600',
                    color: 'var(--brand-primary)'
                  }}>2</div>
                  <div>
                    <h3 className="heading-2" style={{ marginBottom: '12px' }}>Risk Management Module</h3>
                    <p className="body-medium" style={{ marginBottom: '20px' }}>
                      Assess, prioritize and mitigate organizational risks.
                    </p>
                  </div>
                </div>
                
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '30px' }}>
                  <div>
                    <p className="body-small" style={{ color: 'var(--brand-primary)', fontWeight: '600', marginBottom: '8px' }}>
                      Key Differentiators:
                    </p>
                    <ul style={{ listStyle: 'none', padding: 0 }}>
                      <li style={{ marginBottom: '8px', paddingLeft: '20px', position: 'relative' }}>
                        <CheckCircle2 size={16} color="var(--brand-primary)" style={{ position: 'absolute', left: 0, top: '4px' }} />
                        <span className="body-small">Integrated deficiency management</span>
                      </li>
                      <li style={{ paddingLeft: '20px', position: 'relative' }}>
                        <CheckCircle2 size={16} color="var(--brand-primary)" style={{ position: 'absolute', left: 0, top: '4px' }} />
                        <span className="body-small">External risk data import</span>
                      </li>
                    </ul>
                  </div>

                  <div>
                    <p className="body-small" style={{ color: 'var(--brand-primary)', fontWeight: '600', marginBottom: '8px' }}>
                      Pain Points Addressed:
                    </p>
                    <ul style={{ listStyle: 'none', padding: 0 }}>
                      <li style={{ marginBottom: '8px', paddingLeft: '20px', position: 'relative' }}>
                        <div style={{ width: '6px', height: '6px', background: 'var(--brand-primary)', borderRadius: '50%', position: 'absolute', left: '5px', top: '8px' }} />
                        <span className="body-small">Siloed risk data</span>
                      </li>
                      <li style={{ paddingLeft: '20px', position: 'relative' }}>
                        <div style={{ width: '6px', height: '6px', background: 'var(--brand-primary)', borderRadius: '50%', position: 'absolute', left: '5px', top: '8px' }} />
                        <span className="body-small">Lack of risk mitigation visibility</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* Feature 3 */}
              <div style={{
                padding: '40px',
                background: 'rgba(255, 255, 255, 0.03)',
                border: '1px solid var(--border-subtle)'
              }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '20px', marginBottom: '20px' }}>
                  <div style={{
                    width: '48px',
                    height: '48px',
                    background: 'rgba(0, 255, 209, 0.1)',
                    border: '1px solid var(--brand-primary)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                    fontSize: '24px',
                    fontWeight: '600',
                    color: 'var(--brand-primary)'
                  }}>3</div>
                  <div>
                    <h3 className="heading-2" style={{ marginBottom: '12px' }}>Policy & Procedure Management Module</h3>
                    <p className="body-medium" style={{ marginBottom: '20px' }}>
                      Creates, maintains, distributes and enforces policies.
                    </p>
                  </div>
                </div>
                
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '30px' }}>
                  <div>
                    <p className="body-small" style={{ color: 'var(--brand-primary)', fontWeight: '600', marginBottom: '8px' }}>
                      Key Differentiators:
                    </p>
                    <ul style={{ listStyle: 'none', padding: 0 }}>
                      <li style={{ marginBottom: '8px', paddingLeft: '20px', position: 'relative' }}>
                        <CheckCircle2 size={16} color="var(--brand-primary)" style={{ position: 'absolute', left: 0, top: '4px' }} />
                        <span className="body-small">Built-in document editing tools</span>
                      </li>
                      <li style={{ marginBottom: '8px', paddingLeft: '20px', position: 'relative' }}>
                        <CheckCircle2 size={16} color="var(--brand-primary)" style={{ position: 'absolute', left: 0, top: '4px' }} />
                        <span className="body-small">Version control and history tracking</span>
                      </li>
                      <li style={{ paddingLeft: '20px', position: 'relative' }}>
                        <CheckCircle2 size={16} color="var(--brand-primary)" style={{ position: 'absolute', left: 0, top: '4px' }} />
                        <span className="body-small">Approval workflows</span>
                      </li>
                    </ul>
                  </div>

                  <div>
                    <p className="body-small" style={{ color: 'var(--brand-primary)', fontWeight: '600', marginBottom: '8px' }}>
                      Pain Points Addressed:
                    </p>
                    <ul style={{ listStyle: 'none', padding: 0 }}>
                      <li style={{ marginBottom: '8px', paddingLeft: '20px', position: 'relative' }}>
                        <div style={{ width: '6px', height: '6px', background: 'var(--brand-primary)', borderRadius: '50%', position: 'absolute', left: '5px', top: '8px' }} />
                        <span className="body-small">Difficult policy management</span>
                      </li>
                      <li style={{ paddingLeft: '20px', position: 'relative' }}>
                        <div style={{ width: '6px', height: '6px', background: 'var(--brand-primary)', borderRadius: '50%', position: 'absolute', left: '5px', top: '8px' }} />
                        <span className="body-small">Lack of enforcement control</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* Feature 4 */}
              <div style={{
                padding: '40px',
                background: 'rgba(255, 255, 255, 0.03)',
                border: '1px solid var(--border-subtle)'
              }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '20px', marginBottom: '20px' }}>
                  <div style={{
                    width: '48px',
                    height: '48px',
                    background: 'rgba(0, 255, 209, 0.1)',
                    border: '1px solid var(--brand-primary)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                    fontSize: '24px',
                    fontWeight: '600',
                    color: 'var(--brand-primary)'
                  }}>4</div>
                  <div>
                    <h3 className="heading-2" style={{ marginBottom: '12px' }}>Vendor Risk Management</h3>
                    <p className="body-medium" style={{ marginBottom: '20px' }}>
                      Manages risks associated with vendors and customers (Inbound & Outbound).
                    </p>
                  </div>
                </div>
                
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '30px' }}>
                  <div>
                    <p className="body-small" style={{ color: 'var(--brand-primary)', fontWeight: '600', marginBottom: '8px' }}>
                      Key Differentiators:
                    </p>
                    <ul style={{ listStyle: 'none', padding: 0 }}>
                      <li style={{ marginBottom: '8px', paddingLeft: '20px', position: 'relative' }}>
                        <CheckCircle2 size={16} color="var(--brand-primary)" style={{ position: 'absolute', left: 0, top: '4px' }} />
                        <span className="body-small">Streamlined questionnaire assessments</span>
                      </li>
                      <li style={{ marginBottom: '8px', paddingLeft: '20px', position: 'relative' }}>
                        <CheckCircle2 size={16} color="var(--brand-primary)" style={{ position: 'absolute', left: 0, top: '4px' }} />
                        <span className="body-small">Control-level tracking & escalation</span>
                      </li>
                      <li style={{ paddingLeft: '20px', position: 'relative' }}>
                        <CheckCircle2 size={16} color="var(--brand-primary)" style={{ position: 'absolute', left: 0, top: '4px' }} />
                        <span className="body-small">Response recommendations</span>
                      </li>
                    </ul>
                  </div>

                  <div>
                    <p className="body-small" style={{ color: 'var(--brand-primary)', fontWeight: '600', marginBottom: '8px' }}>
                      Pain Points Addressed:
                    </p>
                    <ul style={{ listStyle: 'none', padding: 0 }}>
                      <li style={{ marginBottom: '8px', paddingLeft: '20px', position: 'relative' }}>
                        <div style={{ width: '6px', height: '6px', background: 'var(--brand-primary)', borderRadius: '50%', position: 'absolute', left: '5px', top: '8px' }} />
                        <span className="body-small">Inefficient vendor assessment</span>
                      </li>
                      <li style={{ marginBottom: '8px', paddingLeft: '20px', position: 'relative' }}>
                        <div style={{ width: '6px', height: '6px', background: 'var(--brand-primary)', borderRadius: '50%', position: 'absolute', left: '5px', top: '8px' }} />
                        <span className="body-small">Lack of vendor risk visibility</span>
                      </li>
                      <li style={{ paddingLeft: '20px', position: 'relative' }}>
                        <div style={{ width: '6px', height: '6px', background: 'var(--brand-primary)', borderRadius: '50%', position: 'absolute', left: '5px', top: '8px' }} />
                        <span className="body-small">Inconsistent customer responses</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* The ICA Impact */}
        <section style={{ background: 'var(--bg-primary)', padding: '80px 7.6923%' }}>
          <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
            <h2 className="display-medium" style={{ marginBottom: '60px', textAlign: 'center' }}>
              The <span style={{ color: 'var(--brand-primary)' }}>ICA Impact</span> - Thrive in a Unified Ecosystem
            </h2>

            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
              gap: '30px'
            }}>
              {[
                'Streamline processes, minimize manual effort and reduce the need for costly consultants',
                'Automate tasks, free up resources',
                'Proactively reduce the risk of fines and penalties',
                'Strengthen your security posture and protect sensitive data',
                'Achieve greater visibility into your compliance status and maintain control over your compliance journey'
              ].map((impact, index) => (
                <div key={index} style={{
                  padding: '30px',
                  background: 'rgba(0, 255, 209, 0.05)',
                  border: '1px solid var(--brand-primary)',
                  display: 'flex',
                  gap: '15px',
                  alignItems: 'flex-start'
                }}>
                  <CheckCircle2 size={24} color="var(--brand-primary)" style={{ flexShrink: 0, marginTop: '2px' }} />
                  <p className="body-medium">{impact}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Case Studies Preview */}
        <section style={{ background: 'var(--bg-primary)', padding: '80px 7.6923%' }}>
          <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
            <h2 className="display-medium" style={{ marginBottom: '30px', textAlign: 'center' }}>
              Proven <span style={{ color: 'var(--brand-primary)' }}>Success Stories</span>
            </h2>
            <p className="body-large" style={{ textAlign: 'center', maxWidth: '900px', margin: '0 auto 60px' }}>
              Real results from organizations that transformed their compliance management with ICA
            </p>

            <div style={{ display: 'grid', gap: '30px' }}>
              {[
                { company: 'Mphasis Limited', industry: 'Financial Services & Technology', challenge: '350+ annual audits with complex multi-client compliance needs' },
                { company: 'Toucan Payments', industry: 'Fintech Startup', challenge: 'RBI PA PG compliance with PCI-DSS, ISO 27001 requirements' },
                { company: 'Simbiotik Tech', industry: 'ITES Company', challenge: 'Privacy data handling with constant InfoSec risk assessments' }
              ].map((study, index) => (
                <div key={index} style={{
                  padding: '40px',
                  background: 'rgba(255, 255, 255, 0.03)',
                  border: '1px solid var(--border-subtle)',
                  transition: 'all 0.3s'
                }}
                onMouseEnter={(e) => e.currentTarget.style.borderColor = 'var(--brand-primary)'}
                onMouseLeave={(e) => e.currentTarget.style.borderColor = 'var(--border-subtle)'}
                >
                  <h3 className="heading-2" style={{ marginBottom: '12px', color: 'var(--brand-primary)' }}>{study.company}</h3>
                  <p className="body-small" style={{ marginBottom: '8px', color: 'var(--text-muted)' }}>{study.industry}</p>
                  <p className="body-medium">{study.challenge}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section style={{ background: 'var(--bg-primary)', padding: '80px 7.6923%' }}>
          <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
            <div style={{
              padding: '80px 60px',
              background: 'rgba(0, 255, 209, 0.05)',
              border: '2px solid var(--brand-primary)',
              textAlign: 'center'
            }}>
              <h2 className="display-medium" style={{ marginBottom: '20px' }}>
                Simplify Your <span style={{ color: 'var(--brand-primary)' }}>Compliance Journey</span>
              </h2>
              <p className="body-large" style={{ marginBottom: '40px', maxWidth: '800px', margin: '0 auto 40px' }}>
                Our 20+ years long seasoned prowess and game-changer offering of Technology combined with Human Expertise makes ICA a standout in the segment. We don't meet and greet - we are always there!
              </p>
              <div style={{ display: 'flex', gap: '20px', justifyContent: 'center', flexWrap: 'wrap' }}>
                <Link to="/#contact" className="btn-primary">
                  Schedule a Demo
                </Link>
                <Link to="/#contact" className="btn-secondary">
                  Contact Us
                </Link>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />

      <style>{`
        @media (max-width: 768px) {
          .detail-grid {
            grid-template-columns: 1fr !important;
          }
          .solution-grid {
            grid-template-columns: 1fr !important;
          }
        }
      `}</style>
    </div>
  );
};

export default ICADetail;
